<template>
  <v-layout row wrap class="mb-5">
    <v-flex xs12 class="mb-1">
      <div class="section-header">
        <span v-show="$vuetify.breakpoint.smAndDown">
          <v-icon class="greyIcon">
            store
          </v-icon>
        </span>
        <span class="ml-1 label" :class="{'header-label': $vuetify.breakpoint.smAndDown}">
          {{ $t('message.bizgainOnboarding.summaryPage.BusinessDetails') }}
        </span>
      </div>
      <div class="line" v-if="$vuetify.breakpoint.xsOnly" />
    </v-flex>
    <v-flex xs12 class="app-card elevation-3 rounded">
      <div class="app-card-content custom-card-content">
        <v-layout row wrap>
          <v-flex
            xs12
            mb-3
          >
            <v-layout row wrap class="field">
              <v-flex
                xs12
                md3
              >
                <span class="ml-1 label">
                  {{ $t('message.bizgainOnboarding.summaryPage.BusinessName') }}:
                </span>
              </v-flex>
              <v-flex
                xs12
                md9
              >
                <v-layout>
                  <v-flex xs1 v-if="!isEditBusinessName">
                    <v-icon class="greyIcon">
                      store
                    </v-icon>
                  </v-flex>
                  <v-flex xs9 v-show="!isEditBusinessName">
                    <span class="label detail-field" :class="{'ml-1': $vuetify.breakpoint.xsOnly}">
                      {{ businessAddress.name }}
                    </span>
                  </v-flex>
                  <v-flex xs2>
                    <v-icon
                      v-if="!isEditBusinessName"
                      :class="{ greyIcon: !$vuetify.breakpoint.mdAndUp, themedIcon: $vuetify.breakpoint.mdAndUp}"
                      @click="editBusinessName()"
                    >
                      edit
                    </v-icon>
                  </v-flex>
                </v-layout>
                <v-layout v-if="isEditBusinessName">
                  <v-flex xs12>
                    <v-card class="pa-2 mt-2">
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-text-field
                            :placeholder="$tc('message.bizgainOnboarding.businessName')"
                            class="pt-0 mt-0"
                            name="businessName"
                            counter="25"
                            v-model="businessAddress.name"
                            autocapitalize="off"
                            data-vv-as="Business Name"
                            v-validate.initial="'required|max:25|emojiValidation'"
                            @focus="clearErrorByField($event, 'businessName')"
                            :error-messages="errors.first('businessName')"
                          />
                        </v-flex>
                        <v-flex xs12 md3>
                          <v-btn
                            depressed
                            :disabled="errors.items.length > 0"
                            :loading="isLoadingBusinessNameUpdate"
                            class="mx-0 next-btn-color next-btn-xs"
                            @click="updateBusinessName()"
                          >
                            Save
                          </v-btn>
                        </v-flex>
                      </v-layout>
                    </v-card>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex
            xs12
            mb-3
          >
            <v-layout row wrap>
              <v-flex
                xs12
                md3
              >
                <span class="ml-1 label">
                  {{ $t('message.bizgainOnboarding.summaryPage.BusinessType') }}:
                </span>
              </v-flex>
              <v-flex
                xs12
                md9
              >
                <v-layout v-if="!isEditBusinessType">
                  <v-flex xs1>
                    <v-icon class="greyIcon">
                      category
                    </v-icon>
                  </v-flex>
                  <v-flex xs9>
                    <span class="label detail-field" :class="{'ml-1': $vuetify.breakpoint.xsOnly}">
                      {{ businessTypeStr }}
                    </span>
                  </v-flex>
                  <v-flex xs2>
                    <v-icon
                      :class="{ greyIcon: !$vuetify.breakpoint.mdAndUp, themedIcon: $vuetify.breakpoint.mdAndUp}"
                      @click="isEditBusinessType = true"
                    >
                      edit
                    </v-icon>
                  </v-flex>
                </v-layout>
                <v-layout v-if="isEditBusinessType">
                  <v-flex xs12>
                    <business-type
                      :is-summary="true"
                      :hide-business-type-label="false"
                      @update-businessType="updateBusinessType"
                      @update-loadingBusinessCategories="updateLoadingBusinessCategories"
                      @save-businessType="updateBusinessType"
                    >
                      <template v-slot:btn>
                        <v-layout row wrap>
                          <v-flex xs12 md3>
                            <v-btn
                              depressed
                              :loading="isLoadingUpdateBusinessType"
                              class="mx-0 next-btn-color next-btn-xs"
                              @click="updateBusinessTypeDetails()"
                            >
                              Save
                            </v-btn>
                          </v-flex>
                        </v-layout>
                      </template>
                    </business-type>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex
            xs12
            mb-3
          >
            <v-layout row wrap>
              <v-flex
                xs12
                md3
                align-center
              >
                <span class="ml-1 label">
                  {{ $t('message.bizgainOnboarding.summaryPage.BusinessLogo') }}:
                </span>
              </v-flex>
              <v-flex
                xs12
                md9
              >
                <v-layout>
                  <v-flex
                    xs10
                    class="logo-preview"
                    shrink
                    v-if="isLogoAvailable"
                  >
                    <v-card
                      flat
                      class="s-logo-preview d-flex elevation-2"
                      style="height: 110px; width: 110px"
                    >
                      <div v-if="businessLogo && !isImageUploading">
                        <v-img
                          contain
                          :src="businessLogo.url"
                          aspect-ratio="1"
                          class="white"
                        />
                      </div>
                      <div
                        v-else-if="isImageUploading"
                        fill-height
                        align-center
                        justify-center
                        ma-0
                      >
                        <v-progress-circular
                          :size="40"
                          :width="3"
                          indeterminate
                          color="primary"
                        />
                      </div>
                      <span
                        v-if="businessLogo"
                        class="white--text grey lighten-1 px-1 caption logo-dimension"
                      >
                        {{ `${businessLogo.dimension.height} x ${businessLogo.dimension.width}` }}
                      </span>
                    </v-card>
                  </v-flex>
                  <v-flex xs10 v-if="!isLogoAvailable">
                    <business-images
                      @firstImageProcessed="setIsFirstImageLoaded"
                      @logoSelected="setLogoFromSuggestions"
                      @uploadImage="uploadLogo"
                      @showInvalidBusinessLogoAlert="enableInvalidLogoAlert"
                      @setIsImageUploading="setIsImageUploading"
                      @initialize="setInitializationCompleted"
                      :is-small-suggestion="false"
                      :is-google-map-image-set="false"
                      :is-for-summary="false"
                      ref="businessImage"
                    >
                      <template v-slot:open-dialog>
                        <v-btn
                          depressed
                          class="no-background-hover pa-0"
                          color="primary"
                          outline
                          :loading="isImageUploading"
                        >
                          {{ $tc('message.upload') }}
                        </v-btn>
                      </template>
                    </business-images>
                  </v-flex>
                  <v-flex xs2>
                    <v-layout
                      row
                      wrap
                      align-center
                      class="justify-center"
                      v-if="isLogoAvailable"
                    >
                      <business-images
                        @firstImageProcessed="setIsFirstImageLoaded"
                        @logoSelected="setLogoFromSuggestions"
                        @uploadImage="uploadLogo"
                        @showInvalidBusinessLogoAlert="enableInvalidLogoAlert"
                        @setIsImageUploading="setIsImageUploading"
                        @initialize="setInitializationCompleted"
                        :is-small-suggestion="false"
                        :is-google-map-image-set="false"
                        :is-for-summary="true"
                        ref="businessImage"
                      >
                        <template v-slot:open-dialog>
                          <v-icon
                            :class="{ greyIcon: !$vuetify.breakpoint.mdAndUp, themedIcon: $vuetify.breakpoint.mdAndUp}"
                          >
                            edit
                          </v-icon>
                        </template>
                      </business-images>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
            <v-layout
              v-if="isNotRecommendedDimension"
            >
              <v-flex md3 />
              <v-flex md9>
                <alert
                  :icon-color="'primary'"
                  :icon-width="`${ !$vuetify.breakpoint.xsOnly ? 'xs1' : 'xs2' }`"
                  :outline="true"
                  :outline-color="'primary'"
                  :message="$t('message.bizgainOnboarding.businessLogoRecommendation',{height:recommendedLogoDimension.height,width:recommendedLogoDimension.width})"
                  :col-width="'xs12 md8 sm12 lg8'"
                />
              </v-flex>
            </v-layout>
            <v-layout
              v-if="isInvalidLogoAlertVisible"
              class="mt-2"
            >
              <bizgain-message-component :type="'error'" :message="$t('message.uploadLogoValidationMesssage')" />
            </v-layout>
          </v-flex>
          <v-flex
            xs12
            :class="{'mb-1': !isGenderSpecific, 'mb-3': isGenderSpecific}"
          >
            <v-layout row wrap>
              <v-flex
                xs12
                md3
              >
                <span class="ml-1 label">
                  {{ $t('message.bizgainOnboarding.summaryPage.BusinessAddress') }}:
                </span>
              </v-flex>
              <v-flex
                xs12
                md9
              >
                <v-layout v-if="!isEditBusinessLocation">
                  <v-flex
                    xs1
                    class="justify-start"
                  >
                    <v-icon class="greyIcon">
                      place
                    </v-icon>
                  </v-flex>
                  <v-flex xs9>
                    <div class="label detail-field" :class="{'ml-1': $vuetify.breakpoint.xsOnly}">
                      {{ getLocationString }}
                    </div>
                  </v-flex>
                  <v-flex xs2>
                    <v-icon
                      v-if="!isEditBusinessLocation"
                      :class="{ greyIcon: !$vuetify.breakpoint.mdAndUp, themedIcon: $vuetify.breakpoint.mdAndUp}"
                      @click="isEditBusinessLocation = true"
                    >
                      edit
                    </v-icon>
                  </v-flex>
                </v-layout>
                <div v-if="isEditBusinessLocation">
                  <business-location
                    :is-summary="true"
                    :edit-address="true"
                    @get-location="updateLocationLoadingStatus"
                    @updated-location="updateNewLocation"
                    @save-location="updateBusinessAddress"
                  >
                    <template v-slot:button>
                      <v-btn
                        depressed
                        :disabled="!businessAddress.postalCode || !businessAddress.city || isLoadingGeoLocation || errors.items.length > 0"
                        :loading="isLoadingUpdateLocation"
                        class="mx-0 next-btn-color next-btn-xs"
                        @click="updateBusinessAddress()"
                      >
                        Save
                      </v-btn>
                    </template>
                  </business-location>
                  <div id="g-map-container" />
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex
            xs12
            mb-1
            v-if="isGenderSpecific"
          >
            <v-layout row wrap>
              <v-flex
                xs12
                md3
              >
                <span class="ml-1 label">
                  {{ $t('message.bizgainOnboarding.summaryPage.ServicesCatteredTo') }}:
                </span>
              </v-flex>
              <v-flex
                xs12
                md9
              >
                <v-layout v-if="!isEditServicescatteredTo">
                  <v-flex xs1>
                    <v-icon class="greyIcon" v-if="gender == 0 || gender == genderTypes['all'].id">
                      wc
                    </v-icon>
                    <v-icon class="greyIcon" v-if="gender == genderTypes['male'].id">
                      man
                    </v-icon>
                    <v-icon class="greyIcon" v-if="gender == genderTypes['female'].id">
                      woman
                    </v-icon>
                  </v-flex>
                  <v-flex xs9>
                    <span class="label detail-field">
                      {{ genderStr }}
                    </span>
                  </v-flex>
                  <v-flex xs2>
                    <v-icon
                      :class="{ greyIcon: !$vuetify.breakpoint.mdAndUp, themedIcon: $vuetify.breakpoint.mdAndUp}"
                      v-if="isGenderSpecific && !isEditServicescatteredTo"
                      @click="isEditServicescatteredTo = true"
                    >
                      edit
                    </v-icon>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex xs12 md3 />
              <v-flex xs12 md9 v-if="isEditServicescatteredTo">
                <v-card class="pa-2 mt-2">
                  <v-layout row wrap>
                    <v-flex xs12>
                      <image-card-row
                        :col-width="'xs6 md3'"
                        :card-height="194"
                        :objects="genderTypes"
                        :select-card-id="selectedGender"
                        @selected-card="updateSelectedGender"
                        img-height="90"
                        img-width="90"
                        :loading="isUpdateGender"
                        class="mt-3"
                      >
                        <template v-slot:title="objectiveTitle">
                          {{ $t(objectiveTitle.title) }}
                        </template>
                        <template v-slot:content="objectiveContent">
                          {{ $t(objectiveContent.content,{content:category.displayName}) }}
                        </template>
                      </image-card-row>
                    </v-flex>
                    <v-flex xs12 md3>
                      <v-btn
                        depressed
                        :disabled="!selectedGender"
                        :loading="isUpdateGender"
                        class="mx-0 next-btn-color next-btn-xs"
                        @click="updateGender()"
                      >
                        Save
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </div>
    </v-flex>
  </v-layout>
</template>
<script>
import { i18n } from '@/lang/';
import Alert from 'Components/Alert/Alert';
import BizgainMessageComponent from 'Components/BizgainMessageComponent/BizgainMessageComponent.vue';
import BusinessLocation from 'Components/BusinessLocation/BusinessLocation.vue';
import BusinessType from 'Components/BusinessType/BusinessType.vue';
import ImageCardRow from 'Components/ImageCardRow/ImageCardRow.vue';
import BusinessImages from 'Features/bizgainOnboarding/components/BusinessImages';
import { bizgainOnboardingRoutes, genderTypes, imageTypes, recommendedLogoDimension } from 'Features/bizgainOnboarding/constants';
import { createImageFormDataRequest, getDimensionOfImage } from 'Features/bizgainOnboarding/helpers/commonHelper';
import { ApiService } from 'Features/bizgainOnboarding/services/apiService';
import { Chat } from 'Mixins/chat';
import clearErrorByField from 'Mixins/clearErrorByField';
import { Validator } from 'vee-validate';
import { VCard, VFlex, VIcon, VImg, VLayout, VProgressCircular, VTextField } from 'vuetify/lib';
import { mapActions, mapGetters } from 'vuex';
const { uploadImage } = new ApiService();

export default {
  mixins: [clearErrorByField, Chat],
  components: {
    VLayout,
    VFlex,
    VIcon,
    VCard,
    VImg,
    VTextField,
    VProgressCircular,
    BusinessImages,
    BusinessLocation,
    BizgainMessageComponent,
    ImageCardRow,
    BusinessType,
    Alert
  },
  data() {
    return {
      businessAddress: {
        name: null,
        location: null,
        addressLine: null,
        postalCode: null,
        areaName: null,
        city: null,
        state: null,
        country: null,
        countryCode: null,
        currencyId: null,
        dxLocationId: null,
        latitude: null,
        longitude: null,
        newCurrencySymbol: null,
        oldPostalCode: null
      },
      isLoadingUpdateLocation: false,
      isLoadingGeoLocation: false,
      bizgainOnboardingRoutes,
      recommendedLogoDimension,
      imageTypes,
      countryCode: null,
      logo: null,
      isBusinessLogoAvailable: null,
      businessTypeStr: '',
      businessType: null,
      servicesCateredTo: 3,
      isGenderSpecific: false,
      genderTypes,
      isEditBusinessName: false,
      isLoadingBusinessNameUpdate: false,
      newBusinessDetails: null,
      isInvalidLogoAlertVisible: false,
      isFirstImageProcessed: false,
      isImagesInitialized: false,
      isImageUploading: false,
      selectedGender: null,
      isUpdateGender: false,
      genderData: null,
      isEditServicescatteredTo: false,
      isEditBusinessLocation: false,
      isEditBusinessType: false,
      isLoadingUpdateBusinessType: false
    }
  },
  async created() {
    if(!this.websiteUrl && this.businessDetails && this.businessDetails.websiteUrl){
      this.setWebsiteUrl(this.businessDetails.websiteUrl);
    }
    if(!this.googlePlaceDetails && this.businessDetails && this.businessDetails.googlePlaceDetail){
      this.setGooglePlaceDetails(this.businessDetails.googlePlaceDetail);
    }
    if(!this.facebookPageDetail && this.businessDetails && this.businessDetails.facebookPageDetail){
      this.setFacebookPageDetail(this.businessDetails.facebookPageDetail);
    }
    this.isBusinessLogoAvailable = this.businessDetails.isBusinessLogoAvailable;
    if(!this.businessLogo && this.businessDetails.logoDetails && this.isBusinessLogoAvailable){
      var image = this.businessDetails.logoDetails;
      let imgDimension = await getDimensionOfImage(image.url);
      image.dimension = {
        height: imgDimension.naturalHeight < this.recommendedLogoDimension.height ? this.recommendedLogoDimension.height : imgDimension.naturalHeight,
        width: imgDimension.naturalWidth < this.recommendedLogoDimension.width ? this.recommendedLogoDimension.width : imgDimension.naturalWidth
      }
      await this.setLogoFromSuggestions(image);
    }
    if (this.businessName) {
      this.businessAddress.name = this.businessName;
    }
    if (this.businessLocation) {
      this.businessAddress.addressLine = this.businessLocation.street;
      this.businessAddress.postalCode = this.businessLocation.zip;
      this.businessAddress.areaName = this.businessLocation.areaName;
      this.businessAddress.city = this.businessLocation.city;
      this.businessAddress.state = this.businessLocation.state;
    }

    if (this.category) {
      let businessCategory = {
        text: this.category.displayName,
        value: this.category
      };
      this.businessType = businessCategory;
      this.businessTypeStr = this.category.displayName;
    }
    if(this.category){
      this.isGenderSpecific = this.category.isGenderSpecific;
    }
    if(this.businessDetails.gender){
      this.servicesCateredTo = this.businessDetails.gender;
    }
    this.selectedGender = this.servicesCateredTo;
    const dict = {
      custom: {
        businessName: {
          max: this.$t('message.bizgainOnboarding.businessNameValidationMessage')
        }
      }
    };

    Validator.localize('en', dict);
    Validator.extend('emojiValidation', {
      getMessage: () => this.$t('message.bizgainOnboarding.specialCharacterValidation'),
      validate: value => { return this.validateBusinessName(value)  }
    });
  },
  computed: {
    ...mapGetters(['isCustomUploadedBusinessLogo', 'businessName', 'businessLocation', 'businessDetails', 'isError', 'currencySymbol', 'businessLogo', 'currentBusinessLogo', 'category', 'services', 'gender', 'websiteUrl', 'googlePlaceDetails', 'facebookPageDetail']),
    isNotRecommendedDimension(){
      return !this.isImageUploading && this.businessLogo && (this.businessLogo.dimension.height < this.recommendedLogoDimension.height || this.businessLogo.dimension.width < this.recommendedLogoDimension.width);
    },
    isLogoAvailable(){
      return this.businessDetails.isBusinessLogoAvailable;
    },
    isLogoPreview(){
      if(!this.businessLogo && !this.isImageUploading) {
        return false;
      } else {
        return true;
      }
    },
    isLogoChanged(){
      if (this.currentBusinessLogo !== null && this.businessLogo !== null)
      {
        if (this.currentBusinessLogo.url === this.businessLogo.url && this.currentBusinessLogo.sessionId === this.businessDetails.sessionId)
        {
          return false;
        }
        else {
          return true;
        }
      } else {
        return true;
      }
    },
    genderStr(){
      switch(this.gender) {
        case this.genderTypes.all.id:
          return i18n.tc('message.bizgainOnboarding.both');
        case this.genderTypes.male.id:
          return i18n.tc('message.bizgainOnboarding.onlyMen');
        case this.genderTypes.female.id:
          return i18n.tc('message.bizgainOnboarding.onlyWomen');
        default:
          return 'Both';
      }
    },
    getLocationString(){
      let location = '';
      if (this.businessAddress.addressLine) {
        location += `${this.businessAddress.addressLine} \n`
      }
      if (this.businessAddress.areaName) {
        location += `${this.businessAddress.areaName}, `
      }
      if (this.businessAddress.city) {
        location += `${this.businessAddress.city}, `
      }
      if (this.businessAddress.state) {
        location += `${this.businessAddress.state} `
      }
      if (this.businessAddress.postalCode) {
        location += `${this.businessAddress.postalCode}`
      }
      return location;
    }
  },
  methods: {
    ...mapActions(['patchBusinessDetails', 'updateMessage', 'clearMessages', 'setBusinessLogo', 'setCurrentBusinessLogo', 'setIsCustomUploadedBusinessLogo', 'setIsBusinessLogoModalEnabled', 'setWebsiteUrl', 'setGooglePlaceDetails', 'setFacebookPageDetail']),
    enableInvalidLogoAlert(value){
      this.isInvalidLogoAlertVisible = value;
    },
    setIsFirstImageLoaded(){
      this.isFirstImageProcessed = true;
    },
    setInitializationCompleted(){
      this.isImagesInitialized = true;
    },
    setIsImageUploading(val){
      this.isImageUploading = val;
    },
    async setLogoFromSuggestions(image){
      try {
        this.isImageUploading = true;
        if (!image.dimension)
        {
          let imgDimension = await getDimensionOfImage(image.url);
          image.dimension = {
            height: imgDimension.naturalHeight,
            width: imgDimension.naturalWidth
          }
        }
        this.setBusinessLogo({ url: image.url, dimension: image.dimension });
        this.setIsCustomUploadedBusinessLogo(false);
      } finally {
        this.isImageUploading = false;
      }
    },
    async uploadLogo(file){
      if(file !== undefined)
      {
        this.isImageUploading = true;
        this.setBusinessLogo(null);
        let logoFormData = createImageFormDataRequest(
          this.businessDetails.sessionId,
          0,
          file,
          null,
          this.imageTypes.logo,
          file.name
        );
        try{
          this.clearMessages();
          let response = await uploadImage(logoFormData);
          this.setBusinessLogo(response.data.originLogoDetails);
          if (this.businessDetails) {
            this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
          }
          var dimension = response.data.originLogoDetails.dimension.width + 'x' + response.data.originLogoDetails.dimension.height;
          var uploadImageData = {
            imageUrl: response.data.originLogoDetails.url,
            dimensions: dimension,
            type: imageTypes.logo
          }
          this.newBusinessDetails.uploadedImages.push(uploadImageData);
          let payload = {
            sessionId: this.businessDetails.sessionId,
            oldBusinessDetails: this.businessDetails,
            newBusinessDetails: this.newBusinessDetails
          };
          await this.patchBusinessDetails(payload);
          this.$refs.businessImage.addCustomUploadedImage(uploadImageData);
          this.setIsCustomUploadedBusinessLogo(true);
          await this.updateLogoDetails();
        }catch{
          this.updateMessage({ type: 'error', message: this.$t('message.bizgainOnboarding.failedToUpdateBusinessLogo')  });
        } finally {
          this.isImageUploading = false;
        }
      }
    },
    async updateLogoDetails(){
      let logoFormData = new FormData();
      logoFormData.append('SessionId', this.businessDetails.sessionId);
      logoFormData.append('TemplateId', 0);
      logoFormData.append('ImageType', this.imageTypes.logo);
      if(!this.businessLogo){
        logoFormData.append('ImageName', 'PlaceHolderBusinessLogo');
      }
      else{
        logoFormData.append('ImageUrl', this.businessLogo.url);
      }
      logoFormData.append('IsAsync', true);
      uploadImage(logoFormData);

      this.setCurrentBusinessLogo({ url: this.businessLogo ? this.businessLogo.url : null, sessionId: this.businessDetails.sessionId });
      if (this.businessDetails) {
        this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
        if(!this.businessLogo){
          this.newBusinessDetails.isBusinessLogoAvailable = false;
        }
        else{
          this.newBusinessDetails.isBusinessLogoAvailable = true;
        }
        //this.setIsBusinessLogoModalEnabled(false);
        let payload = {
          sessionId: this.businessDetails.sessionId,
          oldBusinessDetails: this.businessDetails,
          newBusinessDetails: this.newBusinessDetails
        };
        await this.patchBusinessDetails(payload);
        if(this.isError){
          this.isImageUploading = false;
        }
      }
    },
    updateLoadingBusinessCategories(value){
      this.isLoadingUpdateBusinessType = value;
    },
    goToBusinessLocationDetails(){
      this.$router.push({ name: this.bizgainOnboardingRoutes.BusinessLocationDetails.name, params: { isAppFlow: true } });
    },
    goToBusinessTypeServices(){
      this.$router.push({ name: this.bizgainOnboardingRoutes.BusinessTypeServices.name, params: { isAppFlow: true } });
    },
    goToSelectGender() {
      this.$router.push({ name: this.bizgainOnboardingRoutes.SelectGender.name, params: { isAppFlow: true } });
    },
    editBusinessName() {
      this.isEditBusinessName = !this.isEditBusinessName;
    },
    async updateBusinessName() {
      try {
        this.isLoadingBusinessNameUpdate = true;
        if(this.businessDetails){
          this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
          this.newBusinessDetails.name = this.businessAddress.name;
          if(this.newBusinessDetails.creativeDetail && this.newBusinessDetails.creativeDetail.adDetail && this.newBusinessDetails.creativeDetail.adDetail.textAd){
            this.newBusinessDetails.creativeDetail.adDetail.textAd.businessName = this.businessAddress.name;
            this.newBusinessDetails.creativeDetail.adDetail.textAd.headline3 = this.businessAddress.name;
          }
          await this.updateUserProperties({ firstName: `${this.newBusinessDetails && this.newBusinessDetails.name}` });
          let payload = {
            sessionId: this.businessDetails.sessionId,
            oldBusinessDetails: this.businessDetails,
            newBusinessDetails: this.newBusinessDetails
          };
          await this.patchBusinessDetails(payload);
          this.isLoadingBusinessNameUpdate = false;
          this.isEditBusinessName = false;
          if(!this.isError){
            this.isLocationDetailsUpdate = false;
          }
        }
      }
      catch {
        this.isLoadingBusinessNameUpdate = false;
      }
    },
    updateSelectedGender(type){
      this.selectedGender = type;
    },
    async updateGender() {
      if (this.selectedGender && !this.isUpdateGender) {
        try {
          this.isUpdateGender = true;
          if (this.businessDetails) {
            this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
            if (this.category && this.category.isGenderSpecific) {
              this.newBusinessDetails.gender = this.selectedGender;
              let payload = {
                sessionId: this.businessDetails.sessionId,
                oldBusinessDetails: this.businessDetails,
                newBusinessDetails: this.newBusinessDetails
              };
              await this.patchBusinessDetails(payload);
              if(!this.isError){
                this.isUpdateGender = false;
                this.isEditServicescatteredTo = false;
              }
            }
          }
        } catch {
          this.isUpdateGender = false;
          this.isEditServicescatteredTo = false;
        }
      }
    },
    updateNewLocation(updatedLocation, _) {
      this.businessAddress.addressLine = updatedLocation.addressLine;
      this.businessAddress.areaName = updatedLocation.areaName;
      this.businessAddress.city = updatedLocation.city;
      this.businessAddress.country = updatedLocation.country;
      this.businessAddress.countryCode = updatedLocation.countryCode;
      this.businessAddress.currencyId = updatedLocation.currencyId;
      this.businessAddress.dxLocationId = updatedLocation.dxLocationId;
      this.businessAddress.latitude = updatedLocation.latitude;
      this.businessAddress.longitude = updatedLocation.longitude;
      this.businessAddress.newCurrencySymbol = updatedLocation.newCurrencySymbol;
      this.businessAddress.oldPostalCode = updatedLocation.oldPostalCode;
      this.businessAddress.postalCode = updatedLocation.postalCode;
      this.businessAddress.state = updatedLocation.state;
    },
    async updateBusinessAddress(){
      try {
        this.isLoadingUpdateLocation = true;
        if (this.businessDetails) {
          this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
          this.newBusinessDetails.location.currencyId = this.currencyId;
          this.newBusinessDetails.currencySymbol = this.newCurrencySymbol;
          if (this.businessAddress.dxLocationId) {
            this.newBusinessDetails.location.dxLocationId = this.businessAddress.dxLocationId;
          }
          if (this.businessAddress.addressLine) {
            this.newBusinessDetails.location.street = this.businessAddress.addressLine;
          }
          if (this.businessAddress.state) {
            this.newBusinessDetails.location.state = this.businessAddress.state;
          }
          if (this.businessAddress.country) {
            this.newBusinessDetails.location.country = this.businessAddress.country;
            await this.updateUserProperties({ countryName: this.businessAddress.country });
          }
          if (this.businessAddress.longitude) {
            this.newBusinessDetails.location.longitude = this.businessAddress.longitude;
          }
          if (this.businessAddress.latitude) {
            this.newBusinessDetails.location.latitude = this.businessAddress.latitude;
          }
          if (this.businessAddress.areaName) {
            this.newBusinessDetails.location.areaName = this.businessAddress.areaName;
          }
          if(this.businessAddress.countryCode){
            this.newBusinessDetails.location.countryCode = this.businessAddress.countryCode;
          }
          let payload = {
            sessionId: this.businessDetails.sessionId,
            oldBusinessDetails: this.businessDetails,
            newBusinessDetails: this.newBusinessDetails
          };
          await this.patchBusinessDetails(payload);
          if(!this.isError){
            this.isEditBusinessLocation = false;
            this.isLoadingUpdateLocation = false;
          }
        }
      } catch {
        this.isEditBusinessLocation = false;
        this.isLoadingUpdateLocation = false;
      }
    },
    updateLocationLoadingStatus(isLocationLoading) {
      this.isLoadingGeoLocation = isLocationLoading;
    },
    updateBusinessType(value){
      this.businessType = value;
    },
    async updateBusinessTypeDetails(){
      try {
        this.isEditBusinessType = true;
        this.isLoadingUpdateBusinessType = true;
        if (this.businessDetails) {
          this.newBusinessDetails = JSON.parse(JSON.stringify(this.businessDetails));
          if (this.category) {
            this.newBusinessDetails.category.displayName = this.businessType.value.displayName;
            let payload = {
              sessionId: this.businessDetails.sessionId,
              oldBusinessDetails: this.businessDetails,
              newBusinessDetails: this.newBusinessDetails
            };
            await this.updateUserProperties({ businessType: this.businessType.value.displayName });
            await this.patchBusinessDetails(payload);
            if(!this.isError){
              this.businessTypeStr = this.businessType.value.displayName;
              this.isLoadingUpdateBusinessType = false;
              this.isEditBusinessType = false;
              this.isGenderSpecific = this.category.isGenderSpecific;
            }
          }
        }
      } catch {
        this.isLoadingUpdateBusinessType = false;
        this.isEditBusinessType = false;
      }
    },
    validateBusinessName(value){
      // this is regex for emoji detection. https://stackoverflow.com/questions/10992921/how-to-remove-emoji-code-using-javascript
      if(!value.match(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g))
      {
        return true;
      }
      return false
    }
  }
}
</script>
<style lang="scss" scoped>
.line{
  width: 27%;
  border-bottom: solid;
  border-bottom-color: #54B4D3;
}
.section-header{
  display: flex;
  align-content: center;
}
.header-label{
  padding-top: 2px !important;
}
.greyIcon{
  color: #727891;
}
.themedIcon{
  color: #13abb1;
}
.detail-field{
  color: black;
  align-self: center;
}
.custom-card-content{
  padding-bottom: 0 !important;
}
.logo-preview{
  padding: 2px;
  position:relative;
}
.logo-dimension{
  position: absolute;
  bottom: 0px;
  right: 0px;
}
.remove-default-font-case{
  text-transform: none;
}
.text-decoration-underline{
    text-decoration: underline;
}
::v-deep.business-location-details {
  .business-location-card {
    .v-textarea {
      .v-input__control {
        .v-input__slot{
          margin: 0;
          .v-text-field__slot {
            textarea {
              line-height: 1.38;
              letter-spacing: 0.16px;
              padding: 0;
            }
          }
          &::before{
            border-style: none;
          }
          &::after{
            border-style: none;
          }
        }
        .v-text-field__details {
          display: none;
        }
      }
    }
  }
  .text-position {
    position: absolute;
  }

  .s-logo-preview {
    height: 110px;
    width: 110px;
  }
  #lll{
    display: none  !important;
  }

}
</style>